"use client"

import { useEffect, useState, type CSSProperties, type ReactNode } from "react"
import { Check } from "lucide-react"
import { CareersHeader } from "@/components/max/CareersHeader"
import { useCareerPricing } from "@/components/max/useCareerPricing"
import { LaunchBadge, LaunchLockLine } from "@/components/max/LaunchBadge"
import { CareersFooter } from "@/components/max/CareersFooter"
import { CAREERS_MAX_W, CAREERS_INSET } from "@/lib/career/layout"

// careers.signalmax.ai/pricing is the PUBLIC pricing details page (middleware
// rewrites /pricing -> /max/pricing). Same shell as the landing/FAQ (CareerLogo
// header, smx tokens, footer). One plan, $9.95/month, with a free 7-day trial that
// needs no credit card, everything included. Every feature listed here is a
// capability that actually ships today (feed + drawer + profile builders +
// network/outreach + daily digest); nothing aspirational, no fabricated numbers, no
// "guaranteed internship". "Signup" is repeated CTAs into the existing /signup flow,
// which creates the account and starts the free trial (no card). Copy is dash-free
// per house style.

const MAX_W = CAREERS_MAX_W
const shell: CSSProperties = { maxWidth: MAX_W, margin: "0 auto" }
const INSET = CAREERS_INSET

// ── atoms (mirrors the landing) ──────────────────────────────────────────────
function AButton({ href, variant = "primary", size = "md", children }: { href: string; variant?: "primary" | "secondary"; size?: "md" | "lg"; children: ReactNode }) {
  const dims: CSSProperties = size === "lg" ? { minHeight: 48, padding: variant === "secondary" ? "0 22px" : "0 26px", fontSize: 16 } : { minHeight: 44, padding: "0 18px", fontSize: "var(--type-sm)" }
  const skin: CSSProperties =
    variant === "primary"
      ? { background: "var(--accent)", color: "var(--on-accent, #fff)", border: "1px solid var(--accent)" }
      : { background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border-strong)" }
  return (
    <a href={href} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontWeight: 600, borderRadius: "var(--radius-md)", textDecoration: "none", lineHeight: 1, whiteSpace: "nowrap", ...dims, ...skin }}>
      {children}
    </a>
  )
}

function Eyebrow({ accent, children }: { accent?: boolean; children: ReactNode }) {
  return <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: accent ? "var(--accent-text)" : "var(--text-muted)" }}>{children}</span>
}

function Tick({ muted }: { muted?: boolean }) {
  return (
    <span style={{ flexShrink: 0, display: "grid", placeItems: "center", width: 20, height: 20, marginTop: 1, borderRadius: "50%", background: muted ? "var(--surface-sunken)" : "color-mix(in srgb, var(--status-success-fill) 18%, transparent)" }}>
      <Check className="size-3" style={{ color: muted ? "var(--text-muted)" : "var(--status-success-text)" }} strokeWidth={3} />
    </span>
  )
}

// one feature row: name + one-line description of what it actually does
function Feature({ name, desc }: { name: string; desc: string }) {
  return (
    <li style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <Tick />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)" }}>{name}</span>
        <span style={{ display: "block", fontSize: "var(--type-xs)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>{desc}</span>
      </span>
    </li>
  )
}

type Group = { eyebrow: string; title: string; items: { name: string; desc: string }[]; isNew?: boolean }
const GROUPS: Group[] = [
  {
    eyebrow: "01 · Your feed",
    title: "A daily match engine, not a job board",
    items: [
      { name: "Your full ranked feed", desc: "Every real, open internship that fits you, best matches first. Card and table views, sort and filter however you want." },
      { name: "Refreshed every day", desc: "New openings in, closed and expired roles out. You are looking at live postings, not a stale list." },
      { name: "Eligibility on every posting", desc: "Each role is checked against its actual class-year, term, and degree requirements, so you know if you qualify before you apply." },
      { name: "Fit scores with reasons", desc: "Ranked on your major, skills, and coursework, with a plain-English breakdown of what earned every point." },
      { name: "Eligible-only filter", desc: "Hide the roles that already ruled you out and see only the ones actually worth your time." },
    ],
  },
  {
    eyebrow: "02 · Every role",
    title: "Open any role and see the whole picture",
    items: [
      { name: "Why this score", desc: "The requirement-by-requirement breakdown behind each match: what you meet, what you miss, and what each factor contributed." },
      { name: "Tailor your resume", desc: "Rework your resume for a specific role and see the gaps, the changes and classes, that would open up more matches." },
      { name: "People who may help", desc: "Recruiters, alumni, and connections at the company, surfaced for each opportunity so you know who to reach." },
      { name: "AI-drafted intro notes", desc: "A grounded LinkedIn connection note or message, ready to copy or send from your own connected LinkedIn after you review it." },
      { name: "One-click apply link", desc: "Straight to the real posting when you are ready to apply." },
    ],
  },
  {
    eyebrow: "03 · Your profile",
    title: "Build it once, match everywhere",
    items: [
      { name: "Academics and coursework", desc: "School, major, graduation year, and your classes, imported fast, so matching knows what you have actually taken." },
      { name: "Experience and projects", desc: "Roles, internships, and projects that strengthen your fit and your outreach." },
      { name: "Skills", desc: "The skills that drive your scores, and the ones worth adding to unlock more roles." },
      { name: "Resume on file", desc: "Upload your resume for tailoring and feedback, and keep it ready for every application." },
    ],
  },
  {
    eyebrow: "04 · Stay ahead",
    title: "New matches come to you",
    items: [
      { name: "Daily email alerts", desc: "Opt in and get at most one digest a day, and only when a new internship you qualify for shows up." },
      { name: "Only what fits", desc: "Tune alerts to your strongest matches so you are applying in the first days, not the last." },
      { name: "Application and outreach tracking", desc: "Track where every application stands, from saved through applied, interviewing and offer, and who you have reached out to." },
    ],
  },
  {
    eyebrow: "05 · Ask Max",
    title: "Answers from your profile, not the internet",
    isNew: true,
    items: [
      { name: "Grounded in your data", desc: "Ask about your matches, your gaps or the market and get an answer built from your profile, your resume and live postings, not generic advice." },
      { name: "Every answer cites its source", desc: "Each claim names the screen it came from, so you can go check it yourself." },
      { name: "A next action, every time", desc: "Answers end with something to do, not a summary of what you already knew." },
      { name: "It says when it does not know", desc: "When the data is thin, Ask Max tells you, rather than filling the gap with a guess." },
    ],
  },
]

export default function PricingClient() {
  const pricing = useCareerPricing()
  const [coverage, setCoverage] = useState<{ internships_tracked: number; companies_covered: number } | null>(null)

  // Real live counts, best-effort. Hidden if unavailable, so no fake number ever
  // renders (matches the landing's "Live coverage" honesty rule).
  useEffect(() => {
    let active = true
    fetch("/api/career/coverage")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (active && j && j.ok && j.internships_tracked > 0) setCoverage({ internships_tracked: j.internships_tracked, companies_covered: j.companies_covered })
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  return (
    <div className="theme-max smx" style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text-primary)", fontSize: "var(--type-base)" }}>
      {/* header */}
      <CareersHeader />

      {/* hero + price */}
      <section style={{ ...shell, padding: `clamp(40px, 6vw, 80px) ${INSET} clamp(24px, 4vw, 40px)`, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "clamp(28px, 4vw, 56px)", alignItems: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "var(--space-4)", minWidth: 0, animation: "ci-rise 500ms var(--ease-out) both" }}>
          <Eyebrow accent>Pricing</Eyebrow>
          <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(32px, 4.6vw, 54px)", lineHeight: 1.04, letterSpacing: "-0.035em", textWrap: "pretty" }}>One plan. Everything included.</h1>
          <p style={{ margin: 0, maxWidth: "46ch", fontSize: "clamp(16px, 1.4vw, 19px)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)", textWrap: "pretty" }}>
            Start with a free {pricing.trialDays}-day trial, no credit card. No tiers, no add-ons, no upsells. One price unlocks the whole product: your full ranked feed, eligibility on every posting, fit scores, resume tailoring, the people who can help, and daily alerts.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
            <AButton href="/signup" size="lg">Start your free {pricing.trialDays}-day trial</AButton>
            <AButton href="/try" variant="secondary" size="lg">Try a sample first</AButton>
          </div>
          <p style={{ margin: 0, fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)", lineHeight: 1.6 }}>Free {pricing.trialDays}-day trial, no credit card · then {pricing.effective.label}/{pricing.effective.interval} · cancel anytime</p>
          {coverage && (
            <div style={{ display: "flex", gap: "clamp(20px, 4vw, 40px)", paddingTop: "var(--space-3)", borderTop: "1px solid var(--border-subtle)", width: "100%" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-2xl)", fontWeight: 600, letterSpacing: "-0.02em" }}>{coverage.internships_tracked.toLocaleString("en-US")}</span>
                <span style={{ fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>Internships tracked</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-2xl)", fontWeight: 600, letterSpacing: "-0.02em" }}>{coverage.companies_covered.toLocaleString("en-US")}</span>
                <span style={{ fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>Companies covered</span>
              </div>
            </div>
          )}
        </div>

        {/* price card */}
        <div style={{ position: "relative", minWidth: 0, animation: "ci-rise 620ms var(--ease-out) both" }}>
          <div style={{ position: "absolute", inset: "-8% -6% -12% -6%", background: "radial-gradient(60% 55% at 70% 25%, color-mix(in srgb, var(--accent) 20%, transparent), transparent 70%)", filter: "blur(24px)", pointerEvents: "none" }} />
          <div style={{ position: "relative", overflow: "hidden", background: "var(--surface)", border: "1px solid var(--accent)", borderRadius: "var(--radius-xl)", padding: "clamp(24px, 3vw, 32px)", boxShadow: "var(--elevation-3, 0 24px 60px -20px rgba(0,0,0,0.5))", display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)" }}>
              <Eyebrow accent>{pricing.promoActive ? pricing.promoName : "Full access"}</Eyebrow>
              {pricing.promoActive ? <LaunchBadge pricing={pricing} /> : <span style={{ display: "inline-flex", alignItems: "center", fontSize: "var(--type-2xs)", fontWeight: 700, padding: "3px 9px", borderRadius: "var(--radius-pill)", background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent-text)", border: "1px solid var(--accent)" }}>For college students</span>}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", flexWrap: "wrap" }}>
              <span style={{ fontFamily: "var(--font-display)", fontSize: "clamp(46px, 6vw, 66px)", fontWeight: 700, letterSpacing: "-0.04em", lineHeight: 1 }}>{pricing.effective.label}</span>
              <span style={{ fontSize: "var(--type-lg)", color: "var(--text-secondary)" }}>/ {pricing.effective.interval}</span>
              {pricing.promoActive && <span style={{ fontSize: "var(--type-sm)", color: "var(--text-muted)" }}>normally <span style={{ textDecoration: "line-through" }}>{pricing.standard.label}</span></span>}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", alignSelf: "flex-start", gap: 8, fontSize: "var(--type-sm)", fontWeight: 600, padding: "6px 12px", borderRadius: "var(--radius-pill)", background: "color-mix(in srgb, var(--status-success-fill) 16%, transparent)", color: "var(--status-success-text)", border: "1px solid color-mix(in srgb, var(--status-success-fill) 34%, transparent)" }}>
              Free {pricing.trialDays}-day trial, no credit card
            </div>
            <LaunchLockLine pricing={pricing} />
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              {[
                "Complete eligibility-checked internship feed",
                "Explainable fit and gap analysis",
                "Role-specific résumé tailoring",
                "People who may help, and personalized outreach",
                "Daily alerts for newly posted matches",
                "Application and outreach tracking",
                "Ask Max, grounded in your profile and the live market",
              ].map((t) => (
                <li key={t} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: "var(--type-sm)", color: "var(--text-secondary)" }}>
                  <Tick />
                  <span style={{ paddingTop: 1 }}>{t}</span>
                </li>
              ))}
            </ul>
            <AButton href="/signup" size="lg">Start your free {pricing.trialDays}-day trial</AButton>
            <p style={{ margin: 0, textAlign: "center", fontSize: "var(--type-2xs)", color: "var(--text-muted)" }}>Sign up with Google or email · no card for your trial · cancel in one click</p>
          </div>
        </div>
      </section>

      {/* free vs full */}
      <section style={{ background: "var(--surface-sunken)", borderTop: "1px solid var(--border-subtle)", borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ ...shell, padding: `clamp(36px, 5vw, 64px) ${INSET}`, display: "flex", flexDirection: "column", gap: "clamp(20px, 3vw, 32px)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            <Eyebrow>Free vs full access</Eyebrow>
            <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 700, letterSpacing: "-0.03em" }}>Start free. Upgrade when you want the whole feed.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "var(--space-4)" }}>
            {/* free */}
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", padding: "clamp(20px, 3vw, 28px)", borderRadius: "var(--radius-xl)", background: "var(--surface)", border: "1px solid var(--border-subtle)", minWidth: 0 }}>
              <div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-lg)", fontWeight: 700 }}>Free tester</div>
                <div style={{ fontSize: "var(--type-sm)", color: "var(--text-muted)", marginTop: 2 }}>No account, no card</div>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                <Feature name="A sample of real matches" desc="Build a quick profile and see a sample of internships you qualify for, ranked by fit." />
                <Feature name="Eligibility and fit preview" desc="See how eligibility checks and fit scoring work on real postings before you sign up." />
                <Feature name="Resume-assisted profile" desc="Drop in your resume to pre-fill the form. It is read only to fill fields and is not saved." />
              </ul>
              <div style={{ marginTop: "auto", paddingTop: "var(--space-2)" }}>
                <AButton href="/try" variant="secondary">Try it free</AButton>
              </div>
            </div>
            {/* full */}
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", padding: "clamp(20px, 3vw, 28px)", borderRadius: "var(--radius-xl)", background: "linear-gradient(120deg, color-mix(in srgb, var(--accent) 10%, var(--surface)), var(--surface) 60%)", border: "1px solid var(--accent)", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-lg)", fontWeight: 700 }}>Full access</div>
                  <div style={{ fontSize: "var(--type-sm)", color: "var(--text-muted)", marginTop: 2 }}>Everything, unlocked</div>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-2xl)", fontWeight: 700, letterSpacing: "-0.03em" }}>{pricing.effective.label}</span>
                  <span style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)" }}>/ {pricing.effective.interval === "week" ? "wk" : "mo"}</span>
                </div>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                <Feature name="Everything in the free tester" desc="Plus the whole product, for real, on your own profile." />
                <Feature name="Your complete ranked feed" desc="Every eligible internship, refreshed daily, not just a sample." />
                <Feature name="Tailoring, gaps, and the network" desc="Resume tailoring per role, gap closers, and the people who can help with AI-drafted intros." />
                <Feature name="Daily alerts and saved outreach" desc="Get told when new matches post and track every conversation." />
              </ul>
              <div style={{ marginTop: "auto", paddingTop: "var(--space-2)" }}>
                <AButton href="/signup">Get started</AButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* everything included, grouped */}
      <section style={{ ...shell, padding: `clamp(40px, 6vw, 80px) ${INSET}`, display: "flex", flexDirection: "column", gap: "clamp(28px, 4vw, 48px)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <Eyebrow accent>Everything included</Eyebrow>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "clamp(26px, 3.2vw, 40px)", fontWeight: 700, letterSpacing: "-0.03em", textWrap: "pretty" }}>Every feature, in one price.</h2>
          <p style={{ margin: "6px 0 0", maxWidth: "56ch", fontSize: "var(--type-base)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>One subscription turns on the whole platform. Here is exactly what you get.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "var(--space-5)" }}>
          {GROUPS.map((g) => (
            <article key={g.title} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", padding: "clamp(22px, 3vw, 28px)", borderRadius: "var(--radius-xl)", background: "var(--surface)", border: `1px solid ${g.isNew ? "color-mix(in srgb, var(--status-info-border) 55%, var(--border-subtle))" : "var(--border-subtle)"}`, minWidth: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                  <Eyebrow>{g.eyebrow}</Eyebrow>
                  {g.isNew && <span className="smx-badge smx-soft t-info" style={{ fontSize: 9, letterSpacing: "0.04em" }}>New</span>}
                </div>
                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "var(--type-lg)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{g.title}</h3>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                {g.items.map((it) => <Feature key={it.name} name={it.name} desc={it.desc} />)}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* billing reassurance */}
      <section style={{ ...shell, padding: `0 ${INSET} clamp(40px, 6vw, 72px)` }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-4)" }}>
          {[
            { t: "Cancel anytime", d: "One click, no email required. You keep your account and profile, so you can come back with the same login next recruiting season." },
            { t: "Secure by Stripe", d: "Payment is handled by Stripe. We never see or store your card details." },
            { t: "Try before you pay", d: "Browse a sample of your real matches free, with no account and no card, so you know it works for you first." },
          ].map((x) => (
            <div key={x.t} style={{ display: "flex", flexDirection: "column", gap: 6, padding: "18px 20px", borderRadius: "var(--radius-lg)", background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Tick />
                <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-base)", fontWeight: 700 }}>{x.t}</span>
              </div>
              <p style={{ margin: 0, fontSize: "var(--type-sm)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* final CTA */}
      <section style={{ ...shell, padding: `0 ${INSET} clamp(48px, 7vw, 96px)` }}>
        <div style={{ position: "relative", overflow: "hidden", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "clamp(20px, 3vw, 40px)", padding: "clamp(28px, 4vw, 48px)", borderRadius: "var(--radius-xl)", border: "1px solid var(--accent)", background: "linear-gradient(120deg, color-mix(in srgb, var(--accent) 12%, var(--surface)), var(--surface) 60%)" }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 700, letterSpacing: "-0.03em" }}>Ready when you are.</h2>
            <p style={{ margin: "8px 0 0", maxWidth: "46ch", fontSize: "var(--type-base)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>Create your account, pick up your full ranked feed, and start applying to the internships you can actually get. Free for {pricing.trialDays} days, no card, then {pricing.effective.label}/{pricing.effective.interval}, cancel anytime.</p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
            <AButton href="/signup" size="lg">Get started</AButton>
            <AButton href="/sample" variant="secondary" size="lg">See a sample first</AButton>
          </div>
        </div>
      </section>

      {/* footer (shared across the whole public careers site) */}
      <CareersFooter />
    </div>
  )
}
